'use client';
import { ComboBox, ComboBoxItem, ComboBoxProps } from '@florencelabs/combo-box';
import { listBoxItemStyles } from '@florencelabs/list-box';
import { Key, useMemo, useState, ReactElement, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncList } from 'react-stately';
import { DrawerAwareMobileCombobox } from '@provider-directory/components/drawer-aware-mobile-combobox/drawer-aware-mobile-combobox';
import {
  FilterDrawerFooter,
  useSearchFilters,
} from '@provider-directory/contexts/SearchFiltersContext';
import { cn } from '@provider-directory/functions/cn';
import { highlightSubstring } from '@provider-directory/functions/string';
import { useIsDesktop } from '@provider-directory/hooks/useBreakpoints';
import { findSpecialties } from '@provider-directory/services/typeaheadSearch';
import { SpecialtyOptionViewModel } from '@provider-directory/types/search.model';

interface Props
  extends Omit<ComboBoxProps<SpecialtyOptionViewModel>, 'children'> {
  value?: string;
  specialtyNames?: string[];
}

export const SpecialtyFilter = ({
  value,
  specialtyNames,
  ...props
}: Props): ReactElement => {
  const [t] = useTranslation();
  const isDesktop = useIsDesktop();
  const specialtyName = specialtyNames?.[0];
  const [selectedKey, setSelectedKey] = useState<
    Exclude<Key, bigint> | undefined | null
  >(value === '' || !specialtyName ? null : value);

  const autoSelectRef = useRef<
    ((e?: React.FocusEvent | React.KeyboardEvent) => void) | null
  >(null);

  const slugOption = useMemo(
    () =>
      specialtyName && selectedKey
        ? ({
            destinationType: 'specialty',
            value: specialtyName,
            slug: selectedKey.toString(),
          } as SpecialtyOptionViewModel)
        : undefined,
    [selectedKey, specialtyName],
  );

  const {
    items: fetchedItems,
    setFilterText,
    filterText: query,
  } = useAsyncList<SpecialtyOptionViewModel>({
    async load({
      signal,
      filterText = '',
    }): Promise<{ items: SpecialtyOptionViewModel[] }> {
      if (filterText.length <= 2) {
        return {
          items: [],
        };
      }

      const specialties = await findSpecialties(filterText, signal);
      return {
        items: specialties,
      };
    },
  });

  // We need to keep the slug item inside this list to prevent
  // infinite loop re-rendering. Always pin the selected item to the top
  // when it appears in results (exact match or partial query).
  const items = useMemo(() => {
    if (query.length === 0) {
      // Preserve fetchedItems during transient empty-query state (e.g. when
      // the ComboBox clears the input on blur before onAutoSelect fires).
      return fetchedItems
        .filter((item) => item.slug !== slugOption?.slug)
        .concat(slugOption ? [slugOption] : []);
    }

    if (slugOption) {
      const matchingFetchedItem = fetchedItems.find(
        (i) => i.slug === slugOption.slug,
      );
      const otherItems = fetchedItems.filter((i) => i.slug !== slugOption.slug);

      if (matchingFetchedItem) {
        // Pin the fetched item to the top
        return [matchingFetchedItem, ...otherItems];
      }

      // Slug not in fetched results — include slugOption to prevent infinite
      // loop re-rendering when the ComboBox can't resolve the selected key
      if (query === slugOption.value) {
        return [slugOption, ...fetchedItems];
      }
    }

    return fetchedItems;
  }, [fetchedItems, slugOption, query]);

  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { setIsOpen } = useSearchFilters();
  const onAutoSelect = (autoSelectedKey: Key) => {
    const item = items.find((i) => i.slug === autoSelectedKey);
    if (!item || item.isDisabled || selectedKey === item.slug) {
      return;
    }

    setIsOpen(false);
    setIsMobileOpen(false);
    handleSelectionChange(item.slug);
  };

  const handleSelectionChange = (option: Exclude<Key, bigint> | null) => {
    if (option === 'clear') {
      setSelectedKey(null);
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- current react-aria-components 1.8.0 API; deprecation via deduped react-aria
      props.onSelectionChange?.(null);
    } else if (!option) {
      setSelectedKey(value ?? null);
    } else {
      setSelectedKey(option);
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- current react-aria-components 1.8.0 API; deprecation via deduped react-aria
      props.onSelectionChange?.(option);
    }
  };

  const commonProps: Omit<
    ComboBoxProps<SpecialtyOptionViewModel>,
    'children'
  > = {
    ...props,
    onInputChange: setFilterText,
    items,
    clearable: true,
    clearKey: 'clear',
    selectedKey,
    onAutoSelect,
    onSelectionChange: handleSelectionChange,
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.select();
    },
    allowsEmptyCollection: true,
    renderEmptyState: () => (
      <div
        className={listBoxItemStyles({
          isDisabled: true,
        })}
        data-testid={
          query.length <= 2
            ? 'search-filter-specialties-option-error-input-length'
            : 'search-filter-specialties-option-error-no-results'
        }
      >
        {query.length <= 2
          ? t('search.errorInputLength')
          : `${t('search.noResults')} '${query}'`}
      </div>
    ),
  };

  const renderComboBoxItem = (
    option: SpecialtyOptionViewModel,
    isMobile = false,
  ) => (
    <ComboBoxItem
      data-testid={`${isMobile ? 'mobile-' : ''}search-filter-${props.name}-option-${option.slug}`}
      id={option.slug}
      aria-label={`${option.value} specialty option`}
      key={option.slug}
      value={option}
      textValue={option.value}
      isDisabled={option.isDisabled}
      className={cn('!max-w-none !whitespace-normal', {
        'text-primary-color': isMobile && option.slug === selectedKey,
      })}
      isMobile={isMobile}
    >
      {option.isDisabled
        ? option.value
        : highlightSubstring(option.value, query, 'font-bold')}
    </ComboBoxItem>
  );

  return isDesktop ? (
    <ComboBox
      testIdPrefix={'search-filter'}
      {...commonProps}
      aria-label={`specialty filter`}
    >
      {(option) => renderComboBoxItem(option, false)}
    </ComboBox>
  ) : (
    <DrawerAwareMobileCombobox
      testIdPrefix={'mobile-search-filter'}
      {...commonProps}
      items={items}
      isOpen={isMobileOpen}
      onOpenChange={setIsMobileOpen}
      onInputChange={setFilterText}
      autoSelectRef={autoSelectRef}
      onClose={() => {
        setFilterText('');
      }}
      aria-label={`specialty filter`}
      placeholder={t('search.submit')}
      popoverFooter={
        <FilterDrawerFooter
          isClearDisabled={!selectedKey}
          onClear={() => {
            setSelectedKey(null);
            handleSelectionChange('clear');
          }}
          onShowResults={() => {
            autoSelectRef.current?.();
          }}
        />
      }
    >
      {(option) => renderComboBoxItem(option, true)}
    </DrawerAwareMobileCombobox>
  );
};
